import { useEffect, type PropsWithChildren } from "react";
import { hashKey, useQuery, useQueryClient } from "@tanstack/react-query";
import { isApiError } from "src/infrastructure/api/http";
import { onSessionExpired } from "src/infrastructure/api/sessionHeader";
import { useAuth } from "src/infrastructure/auth";
import { PageLoader } from "src/components/PageLoader/PageLoader";
import {
  entitlementsQueryOptions,
  portalSessionQueryOptions,
  restartPortalSession,
  sessionQueryKeys,
} from "./session.queries";
import { SessionContext } from "./session.context";
import type { SessionState } from "./session.types";

const AUTH_DISABLED: SessionState = {
  status: "error",
  error: new Error("Authentication is disabled, so there is no portal session."),
};

/**
 * Loads the portal session and entitlements once signed in, showing a loading
 * state until both settle. Errors don't block the app; `useSession()` exposes them.
 * Reference: twosion-ui src/api/useSessionContext.tsx.
 */
export function SessionProvider({ children }: PropsWithChildren) {
  const { isEnabled, isAuthenticated, user, getAccessTokenSilently } = useAuth();
  const userKey = user?.sub ?? "";
  const enabled = isEnabled && isAuthenticated;

  const queryClient = useQueryClient();

  // Registered before the queries, so it is in place before any call can fail.
  useEffect(() => {
    if (!enabled) return;
    return onSessionExpired(async () => {
      try {
        const session = await restartPortalSession(getAccessTokenSilently, userKey);
        queryClient.setQueryData(sessionQueryKeys.portalSession(userKey), session);
      } catch (error) {
        // Load from scratch, so calls never continue without a session id.
        void queryClient.resetQueries({ queryKey: sessionQueryKeys.all });
        throw error;
      }
    });
  }, [enabled, getAccessTokenSilently, queryClient, userKey]);

  // Refetch entitlements after another query's 403 (docs/guides/app-shell.md). Here,
  // not in QueryProvider, as it needs the user. Reference: twosion-ui src/main.tsx.
  useEffect(() => {
    if (!enabled) return;
    const entitlementsKey = sessionQueryKeys.entitlements(userKey);
    const entitlementsHash = hashKey(entitlementsKey);
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      if (event.query.queryHash === entitlementsHash) return; // their own 403 would loop
      if (!isApiError(event.action.error, 403)) return;
      void queryClient.invalidateQueries(
        { queryKey: entitlementsKey, exact: true },
        { cancelRefetch: false }
      );
    });
  }, [enabled, queryClient, userKey]);

  const session = useQuery({
    ...portalSessionQueryOptions(getAccessTokenSilently, userKey),
    enabled,
  });
  // Data, not isSuccess, here and below: a failed background refetch keeps the last good data.
  const sessionLoaded = session.data !== undefined;
  // After the session, so the request carries its X-Session-Id.
  const entitlements = useQuery({
    ...entitlementsQueryOptions(getAccessTokenSilently, userKey),
    enabled: enabled && sessionLoaded,
  });

  if (!isEnabled) {
    return <SessionContext.Provider value={AUTH_DISABLED}>{children}</SessionContext.Provider>;
  }

  if (session.isPending || (sessionLoaded && entitlements.isPending)) {
    return <PageLoader label="Loading session" />;
  }

  const value: SessionState =
    sessionLoaded && entitlements.data !== undefined
      ? { status: "ready", session: session.data, entitlements: entitlements.data }
      : {
          status: "error",
          error: session.error ?? entitlements.error ?? new Error("Session failed to load."),
          // Reset, not refetch, so the loader shows again while it retries.
          retry: () => void queryClient.resetQueries({ queryKey: sessionQueryKeys.all }),
        };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
